import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";

import {
  NotificationDropdown,
  PageHeader,
  StatsCard,
  FilterDropdown,
  FilterButton,
  fadeIn,
  slideInFromRight,
} from "./components";
import socket from "../../utils/socket";

const PendingTasksPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    status: [],
    dueDate: [],
  });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const filterOpen = Boolean(filterAnchorEl);

  // Fetch leads via socket (like Dashboard)
  const fetchLeads = useCallback(() => {
    if (fetchedRef.current) return;

    fetchedRef.current = true;
    setLoading(true);
    setLeads([]);

    socket.off("leads-batch");
    socket.off("leads-complete");
    socket.off("leads-error");

    const emitFetch = () => {
      socket.emit("get-leads-batch", { batchSize: 100 });
    };

    if (socket.connected) {
      emitFetch();
    } else {
      socket.connect();
      socket.once("connect", emitFetch);
    }

    socket.on("leads-batch", (batch) => {
      setLeads((prev) => {
        const next = [...prev, ...batch];
        if (next.length > 0) setLoading(false);
        return next;
      });
    });

    socket.once("leads-complete", () => {
      setLoading(false);
      fetchedRef.current = false;
      socket.disconnect();
    });

    socket.once("leads-error", () => {
      setLoading(false);
      fetchedRef.current = false;
      socket.disconnect();
    });
  }, []);

  useEffect(() => {
    fetchLeads();

    return () => {
      socket.off("leads-batch");
      socket.off("leads-complete");
      socket.off("leads-error");
      socket.disconnect();
      fetchedRef.current = false;
    };
  }, []);

  // Compute pending tasks from leads
  const { pendingStats, pendingTasks } = useMemo(() => {
    let paymentPendingCount = 0;
    let paymentPendingValue = 0;
    let contractSignature = 0;
    let awaitingDocuments = 0;

    const tasks = [];

    leads.forEach((lead) => {
      // Payment pending
      if (lead.payments && lead.payments.length > 0) {
        const pendingPayments = lead.payments.filter(
          (p) => p.status === "pending" || p.status === "Partial"
        );
        if (pendingPayments.length > 0) {
          const totalValue = pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
          paymentPendingCount += pendingPayments.length;
          paymentPendingValue += totalValue;

          pendingPayments.forEach((payment, idx) => {
            if (idx === 0) { // Only add once per lead
              tasks.push({
                id: lead._id,
                priority: "Pending",
                taskDetails: `Payment Pending - ${lead.client_name || lead.name || "Unknown"}`,
                taskId: `Invoice #${payment._id?.slice(-6) || "N/A"}`,
                status: "Invoice Sent",
                statusColor: "#2563EB",
                Statusbgcolor: "#EFF6FF",
                dueDate: "Pending",
                dueDateColor: "#475569",
                value: `₹${(payment.amount / 1000).toFixed(1)}k`,
              });
            }
          });
        }
      }

      // Contract signature / waiting response
      const status = lead.current_status?.key?.toLowerCase() || "";
      if (["follow-up", "negotiation", "proposal-sent"].includes(status)) {
        contractSignature++;
        tasks.push({
          id: lead._id,
          priority: "Pending",
          taskDetails: `Contract Review - ${lead.client_name || lead.name || "Unknown"}`,
          taskId: lead.current_status?.key || "Review",
          status: "Waiting on Client",
          statusColor: "#D97706",
          Statusbgcolor: "#FFFBEB",
          dueDate: "Pending",
          dueDateColor: "#475569",
          value: "—",
        });
      }

      // Awaiting documents (leads with no contact info or incomplete)
      if (!lead.contact || !lead.email) {
        awaitingDocuments++;
        tasks.push({
          id: lead._id,
          priority: "Pending",
          taskDetails: `Awaiting Documents - ${lead.client_name || lead.name || "Unknown"}`,
          taskId: "Contact Information",
          status: "Upload Pending",
          statusColor: "#64748B",
          Statusbgcolor: "#F8FAFC",
          dueDate: "Pending",
          dueDateColor: "#475569",
          value: "—",
        });
      }
    });

    const stats = [
      {
        label: "PAYMENT PENDING",
        value: `₹${(paymentPendingValue / 1000).toFixed(1)}k`,
        subtitle: `Across ${paymentPendingCount} invoices`,
        icon: MonetizationOnOutlinedIcon,
        bgColor: "#F3E8FF",
        iconColor: "#8B5CF6",
      },
      {
        label: "CONTRACT SIGNATURE",
        value: contractSignature.toString(),
        subtitle: "Pending review",
        icon: DescriptionOutlinedIcon,
        bgColor: "#ECFDF5",
        iconColor: "#10B981",
      },
      {
        label: "AWAITING DOCUMENTS",
        value: awaitingDocuments.toString(),
        subtitle: "Onboarding stage",
        icon: FolderOutlinedIcon,
        bgColor: "#FEF3C7",
        iconColor: "#F59E0B",
      },
    ];

    return { pendingStats: stats, pendingTasks: tasks };
  }, [leads]);

  // Filter options
  const statusOptions = ["Invoice Sent", "Waiting on Client", "Upload Pending"];
  const dueDateOptions = ["Pending"];

  // Filter tasks based on search query and active filters
  const filteredTasks = pendingTasks.filter((task) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === "" ||
      task.taskDetails.toLowerCase().includes(searchLower) ||
      task.taskId.toLowerCase().includes(searchLower) ||
      task.status.toLowerCase().includes(searchLower) ||
      task.value.toLowerCase().includes(searchLower);

    const matchesStatus =
      activeFilters.status.length === 0 ||
      activeFilters.status.includes(task.status);

    const matchesDueDate =
      activeFilters.dueDate.length === 0 ||
      activeFilters.dueDate.includes(task.dueDate);

    return matchesSearch && matchesStatus && matchesDueDate;
  });

  const handleBack = () => navigate("/dashboard");
  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const handleStatusFilterChange = (status) => {
    setActiveFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
    setPage(1);
  };

  const handleDueDateFilterChange = (dueDate) => {
    setActiveFilters((prev) => ({
      ...prev,
      dueDate: prev.dueDate.includes(dueDate)
        ? prev.dueDate.filter((d) => d !== dueDate)
        : [...prev.dueDate, dueDate],
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setActiveFilters({ status: [], dueDate: [] });
    setPage(1);
  };

  const activeFilterCount =
    activeFilters.status.length + activeFilters.dueDate.length;

  const rowsPerPage = 10;
  const pageCount = Math.max(1, Math.ceil(filteredTasks.length / rowsPerPage));
  const paginatedTasks = filteredTasks.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleRowClick = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        animation: `${fadeIn} 0.3s ease-out`,
      }}
    >
      <PageHeader title="Pending Tasks & Payments" onBack={handleBack}>
        <NotificationDropdown />
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: "#7C3AED",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            px: 2,
            py: 0.875,
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#6D28D9",
              boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)",
              transform: "translateY(-1px)",
            },
          }}
        >
          New Task
        </Button>
      </PageHeader>

      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 2.5, mb: 3 }}>
          {pendingStats.map((stat, index) => (
            <StatsCard
              key={index}
              index={index}
              label={stat.label}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
              bgColor={stat.bgColor}
              iconColor={stat.iconColor}
              variant="default"
            />
          ))}
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #E2E8F0",
            overflow: "hidden",
            animation: `${fadeIn} 0.4s ease-out both`,
          }}
        >
          {/* Top Bar */}
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #E2E8F0",
              bgcolor: "#fff",
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: 16, color: "#1E293B" }}
            >
              All Pending Tasks
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                placeholder="Search tasks..."
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 220,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    bgcolor: "#fff",
                  },
                }}
              />

              <FilterButton
                onClick={handleFilterClick}
                activeFilterCount={activeFilterCount}
              />

              <FilterDropdown
                anchorEl={filterAnchorEl}
                open={filterOpen}
                onClose={handleFilterClose}
                activeFilters={activeFilters}
                onStatusFilterChange={handleStatusFilterChange}
                onDueDateFilterChange={handleDueDateFilterChange}
                onClearFilters={handleClearFilters}
                statusOptions={statusOptions}
                dueDateOptions={dueDateOptions}
              />
            </Box>
          </Box>

          {/* Table */}
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["PRIORITY", "TASK DETAILS", "STATUS", "DUE DATE", "VALUE", "ACTIONS"].map(
                  (header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#64748B",
                        textTransform: "uppercase",
                        borderBottom: "1px solid #E2E8F0",
                        px: 2,
                        py: 1.75,
                      }}
                    >
                      {header}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: "#64748B" }}>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 6 }}>
                    <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                      No tasks found matching your criteria
                    </Typography>
                    {(searchQuery || activeFilterCount > 0) && (
                      <Button
                        onClick={() => {
                          setSearchQuery("");
                          handleClearFilters();
                        }}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontSize: 13,
                          color: "#7C3AED",
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTasks.map((task, index) => (
                  <TableRow
                    key={task.id}
                    onClick={() => handleRowClick(task.id)}
                    sx={{
                      animation: `${slideInFromRight} 0.35s ease-out ${index * 0.08}s both`,
                      "& td": {
                        px: 2,
                        py: 2,
                        borderBottom: "1px solid #F1F5F9",
                      },
                      "&:hover": { bgcolor: "#F8FAFC" },
                      cursor: "pointer",
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={task.priority}
                        size="small"
                        sx={{
                          bgcolor: "#ECFDF5",
                          color: "#059669",
                          border: "2px solid #D1FAE5",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 26,
                          borderRadius: 2,
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        {task.taskDetails}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        {task.taskId}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1.5,
                          height: 26,
                          borderRadius: 5,
                          bgcolor: task.Statusbgcolor,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: task.statusColor,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {task.status}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: task.dueDateColor,
                          fontWeight: 700,
                        }}
                      >
                        {task.dueDate}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {task.value}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            bgcolor: "primary.main",
                            textTransform: "none",
                            fontSize: 12,
                            fontWeight: 700,
                            borderRadius: 2,
                            boxShadow: "none",
                            "&:hover": { bgcolor: "primary.main" },
                          }}
                        >
                          Send Reminder
                        </Button>

                        <Button
                          size="small"
                          sx={{
                            textTransform: "none",
                            fontSize: 12,
                            color: "#64748B",
                            fontStyle: "bold",
                            fontWeight: 700,
                            "&:hover": {
                              bgcolor: "#F1F5F9",
                              color: "#1E293B",
                            },
                          }}
                        >
                          {task.value !== "—" ? "View Invoice" : "View Contract"}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#F8FAFC",
              borderTop: "1px solid #E2E8F0",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#64748B" }}>
              Showing {paginatedTasks.length} of {filteredTasks.length} pending tasks
              {(searchQuery || activeFilterCount > 0) && " (filtered)"}
            </Typography>

            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, val) => setPage(val)}
              size="small"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                "& .MuiPaginationItem-root": {
                  fontSize: 14,
                  fontWeight: 700,
                  height: 40,
                  minWidth: 40,
                  borderRadius: 3,
                  color: "#64748B",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: "#fff",
                  border: "2px solid #E2E8F0",
                  color: "primary.main",
                  boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
                  "&:hover": { bgcolor: "#F8FAFC" },
                },
                "& .MuiPaginationItem-page": {
                  border: "none",
                  bgcolor: "transparent",
                  "&:hover": { bgcolor: "#F8FAFC" },
                },
                "& .MuiPaginationItem-previousNext": {
                  bgcolor: "#fff",
                  border: "2px solid #E2E8F0",
                  borderRadius: 3,
                  color: "#94A3B8",
                  "&:hover": { bgcolor: "#F8FAFC" },
                },
                "& .MuiPaginationItem-previousNext.Mui-disabled": {
                  borderColor: "#E5E7EB",
                  color: "#CBD5E1",
                },
              }}
            />
          </Box>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default PendingTasksPage;
